/**
 * The shared grid behind Posts mode and Comments mode (SPEC decision 22).
 *
 * The Posts-table wiring verbatim — TanStack Table over TanStack Virtual, fixed row height,
 * offset windows of 200 fetched with `keepPreviousData`, server-side sorting, drag-to-reorder
 * headers — parameterised over the row type instead of copied twice. The two content tables differ
 * only in their registry, their cell renderers and their endpoint; every behaviour below is
 * identical for both, and a second copy would be a second place for them to drift.
 *
 * It reuses `/view`'s `.posts` / `.pt-*` vocabulary: this *is* that grid, on another dataset.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { MAX_POSTS_LIMIT, type ContentColumnDef, type SortDir } from '@lw/shared';
import { ErrorState, HoverTip, SortArrowIcon, Spinner, cx } from '../../components';
import { formatInteger } from '../../lib/format';
import { nextContentSort } from './contentState';
import type { ContentColumnLayoutHandle } from './useContentColumnLayout';

const ROW_HEIGHT = 40;
const PAGE = MAX_POSTS_LIMIT;
const DRAG_THRESHOLD_PX = 4;
const HEADER_TIP_DELAY_MS = 180;

export type ContentTableWindow = { offset: number; limit: number };

export type ContentTableProps<Row, Id extends string> = {
  /** The column registry, in canonical order — labels, kinds, sortability, hover hints. */
  getColumn: (id: Id) => ContentColumnDef;
  /** Every known id, canonical order; drives `columnVisibility`. */
  allIds: readonly Id[];
  /** TanStack defs, one per registry entry, ids matching. */
  columnDefs: ColumnDef<Row>[];
  columnWidth: (id: Id) => number;
  /**
   * The preview column, which takes whatever width is left — `null` for a table whose every column
   * is a fixed width (the comments grid, whose Comment cell is 340px like the posts basis).
   */
  flexColumnId: Id | null;
  layout: ContentColumnLayoutHandle<Id>;
  sort: { key: Id; dir: SortDir };
  onSortChange: (next: { key: Id; dir: SortDir }) => void;
  /** Only these ids answer a header click — the server's sort vocabulary. */
  sortableKeys: readonly string[];
  enabled: boolean;
  queryKeyFor: (window: ContentTableWindow) => readonly unknown[];
  fetchPage: (
    window: ContentTableWindow,
    signal: AbortSignal,
  ) => Promise<{ total: number; rows: Row[] }>;
  onRowClick?: (row: Row, rows: readonly Row[]) => void;
  /**
   * Rendered instead of the grid when the server returns nothing — the whole block, not just a
   * description, because only the caller knows whether the words are "no match" or "nothing yet"
   * and which action clears it (§12.5 rule 5).
   */
  empty: ReactNode;
  errorTitle: string;
  /** Left half of the pinned footer (the sub-row's job in the pill modes). */
  footerLeft: ReactNode;
};

export function ContentTable<Row, Id extends string>({
  getColumn,
  allIds,
  columnDefs,
  columnWidth,
  flexColumnId,
  layout,
  sort,
  onSortChange,
  sortableKeys,
  enabled,
  queryKeyFor,
  fetchPage,
  onRowClick,
  empty,
  errorTitle,
  footerLeft,
}: ContentTableProps<Row, Id>) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [windowIds, setWindowIds] = useState<number[]>([0]);

  const visibleColumns = useMemo(
    () => layout.order.filter((columnId) => !layout.hidden.has(columnId)),
    [layout.order, layout.hidden],
  );

  /* --------------------------------------------------------------- windows */

  const queries = useQueries({
    queries: windowIds.map((windowId) => {
      const window: ContentTableWindow = { offset: windowId * PAGE, limit: PAGE };
      return {
        queryKey: queryKeyFor(window),
        queryFn: ({ signal }: { signal: AbortSignal }) => fetchPage(window, signal),
        enabled: enabled && layout.ready,
        placeholderData: keepPreviousData,
      };
    }),
  });

  // Folds in every window's identity and freshness, so the row map recomputes exactly when a
  // window resolves, errors or is replaced.
  const signature = `${windowIds.join(',')}#${queries
    .map((query) => `${query.status}:${String(query.dataUpdatedAt)}:${String(query.errorUpdatedAt)}`)
    .join(',')}`;

  const { rows, indexOfRow, total, isPending, isError, error } = useMemo(() => {
    const byIndex: [number, Row][] = [];
    let resolvedTotal: number | null = null;
    for (let i = 0; i < windowIds.length; i += 1) {
      const result = queries[i];
      if (result?.data === undefined) continue;
      resolvedTotal ??= result.data.total;
      const base = (windowIds[i] ?? 0) * PAGE;
      result.data.rows.forEach((row, offset) => byIndex.push([base + offset, row]));
    }
    byIndex.sort((a, b) => a[0] - b[0]);
    const map = new Map<number, number>();
    byIndex.forEach(([absoluteIndex], position) => map.set(absoluteIndex, position));
    return {
      rows: byIndex.map(([, row]) => row),
      indexOfRow: map,
      total: resolvedTotal ?? 0,
      isPending: queries.some((q) => q.isPending),
      isError: queries.some((q) => q.isError),
      error: queries.find((q) => q.isError)?.error,
    };
    // `signature` is the identity of every window's result; recomputing on the array itself would
    // run on every render (`useQueries` returns fresh objects).

  }, [signature]);

  /* ----------------------------------------------------------------- table */

  const columnVisibility = useMemo(() => {
    const visible = new Set<string>(visibleColumns);
    return Object.fromEntries(allIds.map((id) => [id, visible.has(id)]));
  }, [visibleColumns, allIds]);

  const table = useReactTable({
    data: rows,
    columns: columnDefs,
    state: { columnOrder: layout.order, columnVisibility },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const tableRows = table.getRowModel().rows;
  const leafColumns = table.getVisibleLeafColumns();

  /* ------------------------------------------------------------ virtualizer */

  const virtualizer = useVirtualizer({
    count: total,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Fetch exactly the window(s) the viewport touches (§5.4).
  const first = virtualItems[0]?.index ?? 0;
  const last = virtualItems[virtualItems.length - 1]?.index ?? 0;
  const neededWindows = useMemo(() => {
    const from = Math.floor(first / PAGE);
    const to = Math.floor(last / PAGE);
    return from === to ? [from] : [from, to];
  }, [first, last]);

  if (neededWindows.join(',') !== windowIds.join(',')) {
    // Render-phase state sync: the virtualizer reports its range during render, and the queries
    // must follow it in the same commit.
    setWindowIds(neededWindows);
  }

  /* ---------------------------------------------------- header interactions */

  const headerRefs = useRef(new Map<string, HTMLDivElement | null>());
  const pinned = layout.pinned;
  const dragState = useRef<{ id: Id; startX: number; moved: boolean } | null>(null);
  const [drag, setDrag] = useState<{ id: Id; overId: Id | null } | null>(null);

  const [headerTip, setHeaderTip] = useState<Id | null>(null);
  const tipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelHeaderTip = useCallback(() => {
    if (tipTimer.current !== null) clearTimeout(tipTimer.current);
    tipTimer.current = null;
    setHeaderTip(null);
  }, []);
  const scheduleHeaderTip = useCallback((columnId: Id) => {
    if (tipTimer.current !== null) clearTimeout(tipTimer.current);
    tipTimer.current = setTimeout(() => setHeaderTip(columnId), HEADER_TIP_DELAY_MS);
  }, []);
  useEffect(
    () => () => {
      if (tipTimer.current !== null) clearTimeout(tipTimer.current);
    },
    [],
  );

  const headerTipRef = useRef<HTMLElement | null>(null);
  headerTipRef.current = headerTip === null ? null : (headerRefs.current.get(headerTip) ?? null);

  const toggleSort = useCallback(
    (columnId: Id) => {
      if (!sortableKeys.includes(columnId)) return;
      onSortChange(nextContentSort(sort, columnId, getColumn(columnId).kind));
    },
    [onSortChange, sort, sortableKeys, getColumn],
  );

  const onHeaderPointerDown = (event: ReactPointerEvent<HTMLDivElement>, columnId: Id) => {
    cancelHeaderTip();
    if (event.button !== 0) return;
    dragState.current = { id: columnId, startX: event.clientX, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onHeaderPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const state = dragState.current;
    if (state === null) return;
    // The pinned column is not draggable. Bail *before* the threshold, so `moved` stays false and
    // pointer-up takes the click branch (no drag styling, and a click-sort would still work).
    if (state.id === pinned) return;
    if (!state.moved && Math.abs(event.clientX - state.startX) < DRAG_THRESHOLD_PX) return;
    state.moved = true;
    let overId: Id | null = null;
    // While scrolled, the columns underneath the sticky pinned header still contain the pointer,
    // so its rect is a dead zone checked first — not merely skipped in the scan below.
    const pinnedHeader = pinned === null ? null : (headerRefs.current.get(pinned) ?? null);
    const pinnedRect = pinnedHeader?.getBoundingClientRect() ?? null;
    const overPinned =
      pinnedRect !== null && event.clientX >= pinnedRect.left && event.clientX <= pinnedRect.right;
    if (!overPinned) {
      for (const [candidate, element] of headerRefs.current) {
        if (element === null || candidate === pinned) continue;
        const rect = element.getBoundingClientRect();
        if (event.clientX >= rect.left && event.clientX <= rect.right) {
          overId = candidate as Id;
          break;
        }
      }
    }
    setDrag({ id: state.id, overId });
  };

  const onHeaderPointerUp = (event: ReactPointerEvent<HTMLDivElement>, columnId: Id) => {
    const state = dragState.current;
    dragState.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (state !== null && !state.moved) {
      setDrag(null);
      toggleSort(columnId);
      return;
    }
    const target = drag?.overId ?? null;
    if (state !== null && target !== null && target !== state.id) {
      const next = layout.order.filter((entry) => entry !== state.id);
      const at = next.indexOf(target);
      next.splice(at < 0 ? next.length : at, 0, state.id);
      layout.setOrder(next);
    }
    setDrag(null);
  };

  /* ------------------------------------------------------------------ render */

  const showEmpty = !isPending && !isError && total === 0;

  return (
    <div className="posts">
      {isError ? (
        <ErrorState
          title={errorTitle}
          error={error}
          onRetry={() => queries.forEach((query) => void query.refetch())}
        />
      ) : null}

      {showEmpty ? empty : null}

      {!showEmpty && !isError ? (
        <>
          <div className="posts__grid" ref={scrollRef}>
            <div className="posts__inner">
              <div className="pt-row pt-row--head" role="row">
                {leafColumns.map((column) => {
                  const columnId = column.id as Id;
                  const def = getColumn(columnId);
                  const sortable = sortableKeys.includes(columnId);
                  const active = sort.key === columnId;
                  const header = table
                    .getHeaderGroups()[0]
                    ?.headers.find((candidate) => candidate.column.id === column.id);
                  return (
                    <div
                      key={column.id}
                      role="columnheader"
                      aria-sort={
                        active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                      }
                      className={cx(
                        'pt-cell pt-cell--head',
                        `pt-cell--${def.align}`,
                        columnId === flexColumnId && 'pt-cell--flex',
                        columnId === pinned && 'pt-cell--pinned',
                        sortable && 'pt-cell--sortable',
                        drag?.id === columnId && 'pt-cell--dragging',
                        drag?.overId === columnId && drag.id !== columnId && 'pt-cell--dropbefore',
                      )}
                      style={
                        columnId === flexColumnId ? undefined : { width: columnWidth(columnId) }
                      }
                      ref={(element) => {
                        headerRefs.current.set(column.id, element);
                      }}
                      onPointerDown={(event) => onHeaderPointerDown(event, columnId)}
                      onPointerMove={onHeaderPointerMove}
                      onPointerUp={(event) => onHeaderPointerUp(event, columnId)}
                      onMouseEnter={() => scheduleHeaderTip(columnId)}
                      onMouseLeave={cancelHeaderTip}
                    >
                      <span className="pt-cell__label">
                        {header === undefined
                          ? def.label
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {sortable && active ? (
                        <span className="pt-cell__arrow" aria-hidden="true">
                          <SortArrowIcon dir={sort.dir} />
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="posts__body" style={{ height: virtualizer.getTotalSize() }}>
                {virtualItems.map((item) => {
                  const position = indexOfRow.get(item.index);
                  const row = position === undefined ? undefined : tableRows[position];
                  return (
                    <div
                      key={item.key}
                      className={cx('pt-row', row === undefined && 'pt-row--skeleton')}
                      role="row"
                      style={{ height: item.size, transform: `translateY(${String(item.start)}px)` }}
                      onClick={(event) => {
                        // A tooltip trigger or a link inside a cell is not a row activation.
                        if (
                          event.target instanceof Element &&
                          (event.target.closest('.tip') !== null ||
                            event.target.closest('a') !== null)
                        ) {
                          return;
                        }
                        if (row !== undefined) onRowClick?.(row.original, rows);
                      }}
                    >
                      {row === undefined
                        ? leafColumns.map((column) => (
                            <div
                              key={column.id}
                              className={cx(
                                'pt-cell',
                                column.id === flexColumnId && 'pt-cell--flex',
                                column.id === pinned && 'pt-cell--pinned',
                              )}
                              style={
                                column.id === flexColumnId
                                  ? undefined
                                  : { width: columnWidth(column.id as Id) }
                              }
                            >
                              <span className="skeleton" />
                            </div>
                          ))
                        : row.getVisibleCells().map((cell) => {
                            const columnId = cell.column.id as Id;
                            const def = getColumn(columnId);
                            return (
                              <div
                                key={cell.id}
                                role="cell"
                                className={cx(
                                  'pt-cell',
                                  `pt-cell--${def.align}`,
                                  columnId === flexColumnId && 'pt-cell--flex',
                                  columnId === pinned && 'pt-cell--pinned',
                                )}
                                style={
                                  columnId === flexColumnId
                                    ? undefined
                                    : { width: columnWidth(columnId) }
                                }
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>
                            );
                          })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <HoverTip
            open={headerTip !== null}
            anchorRef={headerTipRef}
            text={headerTip === null ? '' : getColumn(headerTip).description}
          />
        </>
      ) : null}

      {isPending && !showEmpty ? (
        <div className="row muted posts__loading">
          <Spinner size="sm" />
          Fetching rows…
        </div>
      ) : null}

      {/* Last child of the 100dvh column: the grid scrolls, this stays pinned to the bottom. */}
      <div className="posts__footer">
        <span>{footerLeft}</span>
        <span>{isPending ? 'Loading…' : `${formatInteger(total)} shown`}</span>
      </div>
    </div>
  );
}

export default ContentTable;
